import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useCustomisation } from "../../../context/CustomisationContext";
import { success } from "../../../theme/themePrimitives";
import { useTranslation } from "../../../context/TranslationContext";
import { getShadeColour } from "../../../utils/helpers";

interface NewPasswordFormProps {
  password: string;
  handlePasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  confirmPassword: string;
  handleConfirmPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  passwordError: string;
  confirmPasswordError: string;
  resetPassword: () => void;
  loading: boolean;
  activateAccount: boolean;
  passwordValidation: {
    hasMinLength: boolean;
    hasLowercase: boolean;
    hasUppercase: boolean;
    hasSpecialChar: boolean;
  };
}

export const NewPasswordForm: React.FC<NewPasswordFormProps> = ({
  password,
  handlePasswordChange,
  confirmPassword,
  handleConfirmPasswordChange,
  confirmPasswordError,
  passwordError,
  resetPassword,
  loading,
  activateAccount,
  passwordValidation,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { customisations } = useCustomisation();
  const { translations } = useTranslation();
  const newPasswordTranslations = translations.translations["new-password"];
  const {
    LOGIN: {
      textColorsSettings: loginTextColors,
      inputFieldsSettings,
      buttonsSettings: loginbuttonssettings,
    },
    GPC: {
      textColorsSettings: gpcTextColors,
      buttonsSettings: gpcbuttonssettings,
    },
  } = customisations.customisations;

  const secondaryTextColor =
    loginTextColors?.loginSecondaryTextColor ??
    gpcTextColors?.gpcSecondaryTextColor;
  const secondaryButtonBg =
    loginbuttonssettings?.loginBtnSecondaryBgColor ??
    gpcbuttonssettings?.gpcBtnPrimaryBgColor;
  const secondaryButtonText =
    loginbuttonssettings?.loginBtnSecondaryTextColor ??
    gpcbuttonssettings?.gpcBtnSecondaryTextColor;

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  return (
    <>
      <Stack spacing={1} width="100%" alignItems="center">
        <Typography variant="h5" align="center" color={secondaryTextColor}>
          {activateAccount
            ? newPasswordTranslations.setNewPasswordHeader?.activateAccount
            : newPasswordTranslations.setNewPasswordHeader?.setNewPasswordText}
        </Typography>
        <Typography variant="body1" align="center" color={secondaryTextColor}>
          {activateAccount
            ? newPasswordTranslations.setNewPasswordHeader?.activateAccountDsc
            : newPasswordTranslations.setNewPasswordHeader?.setNewPasswordDsc}
        </Typography>
      </Stack>

      <Stack spacing={2} width="100%">
        <Stack spacing={1}>
          <Typography variant="subtitle2" color={secondaryTextColor}>
            {newPasswordTranslations.setNewPasswordForm?.newPassword}
          </Typography>
          <TextField
            fullWidth
            id="password"
            placeholder="••••••••"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={handlePasswordChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !loading) {
                resetPassword();
              }
            }}
            variant="outlined"
            size="small"
            error={Boolean(passwordError)}
            helperText={passwordError}
            slotProps={{
              input: {
                sx: {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: inputFieldsSettings?.inputBorderColor,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: inputFieldsSettings?.inputBorderColor,
                  },
                  backgroundColor: inputFieldsSettings?.inputBGColor,
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility}
                      edge="end"
                      size="small"
                      sx={{
                        border: "none !important",
                        bgcolor: "transparent !important",
                      }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>
        <Stack spacing={1}>
          <Typography variant="subtitle2" color={secondaryTextColor}>
            {newPasswordTranslations.setNewPasswordForm?.repeatPassword}
          </Typography>
          <TextField
            fullWidth
            id="confirmPassword"
            placeholder="••••••••"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !loading) {
                resetPassword();
              }
            }}
            variant="outlined"
            size="small"
            error={Boolean(confirmPasswordError)}
            helperText={confirmPasswordError}
            slotProps={{
              input: {
                sx: {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: inputFieldsSettings?.inputBorderColor,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: inputFieldsSettings?.inputBorderColor,
                  },
                  backgroundColor: inputFieldsSettings?.inputBGColor,
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={toggleConfirmPasswordVisibility}
                      edge="end"
                      size="small"
                      sx={{
                        border: "none !important",
                        bgcolor: "transparent !important",
                      }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>
        <Stack spacing={1} width={"100%"}>
          <Stack
            direction={"row"}
            spacing={1}
            alignItems={"center"}
            width={"100%"}
          >
            <CheckCircleRoundedIcon
              sx={{
                color: passwordValidation.hasMinLength
                  ? success[600]
                  : "#D0D5DD",
              }}
            />
            <Typography color={secondaryTextColor}>
              {newPasswordTranslations.setNewPasswordForm?.criteria1}
            </Typography>
          </Stack>
          <Stack
            direction={"row"}
            spacing={1}
            alignItems={"center"}
            width={"100%"}
          >
            <CheckCircleRoundedIcon
              sx={{
                color: passwordValidation.hasLowercase
                  ? success[600]
                  : "#D0D5DD",
              }}
            />
            <Typography color={secondaryTextColor}>
              {newPasswordTranslations.setNewPasswordForm?.criteria2}
            </Typography>
          </Stack>
          <Stack
            direction={"row"}
            spacing={1}
            alignItems={"center"}
            width={"100%"}
          >
            <CheckCircleRoundedIcon
              sx={{
                color: passwordValidation.hasUppercase
                  ? success[600]
                  : "#D0D5DD",
              }}
            />
            <Typography color={secondaryTextColor}>
              {newPasswordTranslations.setNewPasswordForm?.criteria3}
            </Typography>
          </Stack>
          <Stack
            direction={"row"}
            spacing={1}
            alignItems={"center"}
            width={"100%"}
          >
            <CheckCircleRoundedIcon
              sx={{
                color: passwordValidation.hasSpecialChar
                  ? success[600]
                  : "#D0D5DD",
              }}
            />
            <Typography color={secondaryTextColor}>
              {newPasswordTranslations.setNewPasswordForm?.criteria4}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Button
          fullWidth
          onClick={resetPassword}
          sx={{
            py: 1.5,
            borderRadius: "12px",
            backgroundColor: `${secondaryButtonBg} !important`,
            color: `${secondaryButtonText}`,
            "&:hover": {
              bgcolor: `${getShadeColour(secondaryButtonBg!, -8)} !important`,
              fontWeight: 700,
            },
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            {!activateAccount
              ? newPasswordTranslations.passwordReset?.btnReset
              : newPasswordTranslations.passwordReset?.btnActivate}
          </Typography>
        </Button>
      )}
    </>
  );
};
